import { Link } from 'react-router-dom';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';

import type { ColDef } from '../../components/DataGrid/types';
import type { DashboardRow, TrackMark } from '../../types/dashoard';
import { listFormatter, zeroFormatter } from './renderers';

const cellClassName = cn('text-body-xs font-semibold text-white');

export const columnDefs: ColDef[] = [
  {
    field: 'isrc',
    headerName: 'ISRC',
    width: 155,
    cellClass: cellClassName,
    sortable: true,
    cellRenderer: (p) => {
      const lock = p.data.trackLock as TrackMark;
      const ignore = p.data.trackIgnore as TrackMark;

      return (
        <div
          data-testid="isrc-container"
          className={cn(
            'flex items-center',
            ignore && 'line-through text-subtle',
          )}
        >
          <span
            data-testid="track-ignore"
            title={
              ignore
                ? `Disabled by: ${(ignore as { owner: { name: string }; createdAt: Date }).owner.name} 
                   on ${(ignore as { owner: { name: string }; createdAt: Date }).createdAt.toLocaleDateString()}`
                : undefined
            }
          >
            <Link to={`../tracks/${p.data.isrc}`} data-testid="isrc-link">
              {p.data.isrc as string}
            </Link>
          </span>
          {lock ? (
            <Icon
              className={cn(
                'ml-sm text-body-xs',
                (lock as { self?: boolean }).self
                  ? 'text-subtle'
                  : 'text-error-alert',
              )}
              data-testid="track-lock-icon"
              type="eye"
              variant="solid"
              title={(() => {
                const lockData = lock as {
                  owner?: { name: string };
                  createdAt?: Date;
                };

                return lockData.owner && lockData.createdAt
                  ? `Locked by: ${lockData.owner.name} on ${lockData.createdAt.toLocaleDateString()}`
                  : undefined;
              })()}
            />
          ) : null}
        </div>
      );
    },
  },
  {
    field: 'artist',
    headerName: 'Artist',
    minWidth: 150,
    flex: 1,
    sortable: true,
    cellRenderer: (p) => {
      if (!p?.data) {
        return null;
      }

      const { artist, overwrites } = p.data as Partial<DashboardRow>;

      return (
        <div>
          {overwrites?.artist ? (
            <Icon
              className="mr-xs text-body-xs text-subtle"
              type="marker"
              title={overwrites?.artist}
            />
          ) : null}
          <span>{artist}</span>
        </div>
      );
    },
  },
  {
    field: 'trackTitle',
    headerName: 'Track Title',
    flex: 1,
    sortable: true,
    minWidth: 200,
    cellRenderer: (p) => {
      if (!p?.data) {
        return null;
      }

      const { trackTitle, overwrites } = p.data as Partial<DashboardRow>;

      return (
        <div>
          {overwrites?.trackTitle ? (
            <Icon
              className="mr-xs text-body-xs text-subtle"
              type="marker"
              title={overwrites?.trackTitle}
            />
          ) : null}
          <span>{trackTitle}</span>
        </div>
      );
    },
  },
  {
    field: 'recType',
    headerName: 'Recording Type',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  {
    field: 'recSubType',
    headerName: 'Recording Sub Type',
    minWidth: 160,
    flex: 1,
    sortable: true,
  },
  {
    field: 'audioPresentation',
    headerName: 'Audio Presentation',
    minWidth: 120,
    flex: 1,
    sortable: true,
    hide: true,
  },
  {
    field: 'gpid',
    headerName: 'GPID(s)',
    filterName: 'GPID',
    cellClass: cellClassName,
    minWidth: 150,
    flex: 1,
    valueFormatter: listFormatter,
    cellRenderer: (p) => {
      if (!p?.data?.gpid) {
        return null;
      }

      const gpids = (p.data as { gpid?: string[] }).gpid ?? [];

      if (gpids.length === 0) {
        return null;
      }

      return (
        <div data-testid="gpid-container" className="flex">
          {gpids.length > 0 && <span data-testid="gpid-value">{gpids[0]}</span>}
          {gpids.length > 1 && (
            <span data-testid="gpid-more" className="ml-sm">
              (+{gpids.length - 1})
            </span>
          )}
        </div>
      );
    },
    sortable: true,
  },
  {
    headerName: 'WMG1 Label',
    field: 'wmg1Labels',
    hide: true,
    valueFormatter: listFormatter,
  },
  {
    field: 'majorLabel',
    headerName: 'Major Label',
    flex: 1,
    cellClass: cellClassName,
    valueFormatter: listFormatter,
    sortable: true,
    minWidth: 180,
  },
  {
    field: 'presentationLabel',
    headerName: 'Presentation Label',
    cellClass: cellClassName,
    valueFormatter: listFormatter,
    flex: 1,
    sortable: true,
    minWidth: 180,
  },
  {
    field: 'repertoireOwner',
    headerName: 'Repertoire Owner',
    flex: 1,
    sortable: true,
    hide: true,
  },
  {
    field: 'releaseType',
    headerName: 'Release Type(s)',
    filterName: 'Release Type',
    width: 140,
    cellClass: cellClassName,
    valueFormatter: listFormatter,
    sortable: true,
  },
  {
    field: 'paFlag',
    headerName: 'PA Flag',
    width: 120,
    sortable: true,
    hide: true,
  },
  {
    field: 'explicit',
    headerName: 'Explicit',
    flex: 1,
    sortable: true,
    hide: true,
  },
  {
    field: 'trackRightsTerritories',
    headerName: 'Track Rights Terr.',
    flex: 1,
    valueFormatter: listFormatter,
    hide: true,
  },
  {
    field: 'deliveryTerritoryCount',
    headerName: '# Delivery Terr.',
    width: 140,
    sortable: true,
  },
  {
    field: 'deliveryTerritoryList',
    headerName: 'Delivery Terr.',
    flex: 1,
    minWidth: 140,
    valueFormatter: listFormatter,
  },
  {
    field: 'missedTerritoriesCountAlt',
    headerName: '# Missed Terr.',
    width: 120,
    sortable: true,
  },
  {
    field: 'missedTerritoriesAlt',
    headerName: 'Missed Terr.',
    flex: 1,
    minWidth: 140,
    valueFormatter: listFormatter,
  },
  {
    field: 'trackRightsTerritoryCount',
    headerName: '# Track Rights Terr.',
    width: 140,
    sortable: true,
    hide: true,
  },
  {
    field: 'partnerCount',
    headerName: '# Partners',
    minWidth: 100,
    sortable: true,
    flex: 1,
  },
  {
    field: 'partnerList',
    headerName: 'Partners',
    flex: 1,
    valueFormatter: listFormatter,
    minWidth: 140,
  },
  {
    field: 'missedAppleTerritoriesCountAlt',
    headerName: 'Apple: # Missed Terr.',
    minWidth: 180,
    flex: 1,
    sortable: true,
  },
  {
    field: 'missedAppleTerritoriesAlt',
    headerName: 'Apple: Missed Terr.',
    flex: 1,
    minWidth: 140,
    valueFormatter: listFormatter,
  },
  {
    field: 'appleTerritoryCount',
    headerName: 'Apple: # Terr.',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  {
    field: 'missedSpotifyTerritoriesCountAlt',
    headerName: 'Spotify: # Missed Terr.',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  {
    field: 'missedSpotifyTerritoriesAlt',
    headerName: 'Spotify: Missed Terr.',
    flex: 1,
    valueFormatter: listFormatter,
    minWidth: 150,
  },
  {
    field: 'spotifyTerritoryCount',
    headerName: 'Spotify: # Terr.',
    minWidth: 140,
    flex: 1,
    sortable: true,
  },
  {
    field: 'lastCompleteWeekStreams',
    headerName: 'Last Complete Week Streams',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'momPctRevenueNet',
    headerName: 'Month over Month % Revenue Change (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'lmrNetValue',
    headerName: 'Last Month Revenue (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'llmrNetValue',
    headerName: 'Last to Last Month Revenue (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'revenueYtdUsdNet',
    headerName: 'Revenue Year to Date, USD (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'monthlyTrendFixesAddedRevenue',
    headerName: 'Fixes: Added Revenue',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'monthlyTrendRevenueNet',
    headerName: 'Monthly Revenue Trend (Net)',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
  {
    field: 'monthlyTrendFixesAddedStreams',
    headerName: 'Fixes: Added Streams',
    width: 120,
    sortable: true,
    hide: true,
    valueFormatter: zeroFormatter,
  },
];
